import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, vModelText as _vModelText, withDirectives as _withDirectives, createBlock as _createBlock, createVNode as _createVNode, Fragment as _Fragment, unref as _unref, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "w-full flex justify-between items-center mb-8" }
const _hoisted_2 = { class: "flex-1" }
const _hoisted_3 = { key: 0 }
const _hoisted_4 = { class: "font-bold text-left text-black text-lg" }
const _hoisted_5 = {
  key: 1,
  class: "flex items-center gap-2"
}
const _hoisted_6 = { class: "flex items-center gap-6 ml-4" }
const _hoisted_7 = { class: "w-full flex flex-col lg:flex-row justify-between items-start gap-4 mb-8" }
const _hoisted_8 = { class: "w-full lg:w-4/5" }
const _hoisted_9 = { class: "w-full lg:w-1/5" }
const _hoisted_10 = { class: "sm:gap-14 gap-0 border-b flex sm:justify-start justify-between text-white select-none" }
const _hoisted_11 = { class: "flex flex-col gap-10 sm:h-[570px] justify-between" }
const _hoisted_12 = { key: 0 }
const _hoisted_13 = { key: 1 }
const _hoisted_14 = { key: 2 }

import { ref, onMounted, computed } from 'vue'
import { useToast } from 'vue-toastification'
import { useErrorToast } from '@/composables/useErrorToast'

import { mdiFilter } from '@mdi/js'
import EditIcon from '@/assets/icons/edit.vue'
import CheckIcon from '@/assets/icons/check-icon.vue'
import CloseIcon from '@/assets/icons/close.vue'

import { useDsPolicyStore } from '@/pinia-store/useDsPolicyStore'

import SearchInput from '@/components/designSystem/Inputs/SearchInput.vue'
import OutlinedButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import FilterModal from '../FilterDsPoliciesModal/FilterModal.vue'

import Authorized from './Authorized/authorized.tab.vue'
import Blocked from './Blocked/blocked.tab.vue'
import Domain from './Domain/index.vue'
import Spinner from '@/components/others/Spinner.vue'
import { useRoute, useRouter } from 'vue-router'
import PATHS from '@/router/paths'
import { usePermissionsStore } from '@/pinia-store/usePermissionsStore'
import { AuthorizationPermissions, AuthorizationResourcesAndSubjects } from '@/types/authorization'

interface Company {
  title: string
  value: string
}

interface Filters {
  search: string
  companies: Company[]
  isActive?: string
  authorizationStatus?: string
}


export default /*#__PURE__*/_defineComponent({
  __name: 'ManageDoctorAuthorizationPolicies',
  setup(__props) {

const tab = ref(1)
const loading = ref(false)
const activeClass = ref('inline-block py-4 border-b-2 border-primary text-black font-semibold')
const inactiveClass = ref('inline-block py-4 text-gray-500')
const filterModalProps = ref(false)
const isEditing = ref(false)
const policyName = ref('Nome da Política')
const tempPolicyName = ref(policyName.value)

const policyStore = useDsPolicyStore()
const toast = useToast()
const { notifyError } = useErrorToast()
const route = useRoute()
const router = useRouter()
const policyId = ref(Number(route.params.ds_policy_id))
const textInput = ref(route.query.q ? String(route.query.q) : '')
const permissionsStore = usePermissionsStore()

const canEditPolicy = computed(() => {
  return permissionsStore.checkAnyRessources({
    permission: AuthorizationPermissions.StationCreating,
    resourceType: AuthorizationResourcesAndSubjects.Schedule
  })
})

onMounted(async () => {
  let policy = policyStore.currentPolicy

  textInput.value = route.query.q ? String(route.query.q) : ''

  if (!policy) {
    try {
      policy = await policyStore.fetchDsPolicyById(Number(route.params.ds_policy_id))
    } catch (e) {
      notifyError(e, 'Não foi possível carregar a política de restrição.')
      return
    }
  }

  if (policy) {
    policyName.value = policy.name
    policyId.value = policy.id
  } else {
    console.warn('Nenhuma política encontrada')
  }
})

const startEditing = () => {
  tempPolicyName.value = policyName.value
  isEditing.value = true
}

const saveEdit = async () => {
  const newName = tempPolicyName.value.trim()
  if (!newName || !policyStore.currentPolicy) return
  try {
    loading.value = true
    const updated = await policyStore.clpatchDsPolicy(
      policyStore.currentPolicy.id,
      policyStore.currentPolicy.companyOwner.id,
      {
        name: newName
      }
    )
    policyName.value = updated.name
    toast.success('Nome da política atualizado com sucesso!')
    isEditing.value = false
  } catch (e) {
    notifyError(e, 'Não foi possível atualizar o nome da política de restrição.')
  } finally {
    loading.value = false
  }
}

const cancelEdit = () => {
  tempPolicyName.value = policyName.value
  isEditing.value = false
}

const changeTab = async (newTab: number) => {
  if (tab.value !== newTab) {
    tab.value = newTab
    await handleTabChange()
  }
}

// Trata o próprio erro: tem 3 callers (troca de aba sem catch, busca e filtros) e a
// mensagem certa depende da aba ativa.
const handleTabChange = async () => {
  if (tab.value === 1) {
    try {
      await policyStore.fetchAuthorizedDoctors(policyId.value, {
        ...route.query
      })
    } catch (e) {
      notifyError(e, 'Não foi possível carregar os médicos autorizados da política.')
    }
  }

  if (tab.value === 2) {
    try {
      await policyStore.fetchBlockedDoctors(policyId.value, {
        ...route.query
      })
    } catch (e) {
      notifyError(e, 'Não foi possível carregar os médicos bloqueados da política.')
    }
  }
}

const onSearchByTabIndex = async () => {
  await router.push({
    path: `${PATHS.MANAGE_DOCTOR_AUTHORIZATION_POLICIES}/${policyId.value}`,
    query: { q: textInput.value, company_ids: route.query.company_ids || [] }
  })
  await handleTabChange()
}

const openFilterModal = () => {
  filterModalProps.value = true
}

const closeFilterModal = () => {
  filterModalProps.value = false
}

const appliedFilters = ref<Filters>({
  search: '',
  companies: []
})

const applyFilters = async (filters: Filters) => {
  appliedFilters.value = filters

  const query: Record<string, any> = {
    q: route.query.q,
    company_ids: filters.companies.map((c) => c.value).join(',')
  }

  if (filters.authorizationStatus) query.authorizationStatus = filters.authorizationStatus
  if (filters.isActive) query.isActive = filters.isActive === 'active' ? true : false

  await router.push({
    path: `${PATHS.MANAGE_DOCTOR_AUTHORIZATION_POLICIES}/${policyId.value}`,
    query
  })

  try {
    loading.value = true
    // `handleTabChange` já notifica a falha de carga da aba.
    await handleTabChange()
  } finally {
    filterModalProps.value = false
    loading.value = false
  }
}

return (_ctx: any,_cache: any) => {
  return (canEditPolicy.value)
    ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
        _createElementVNode("div", _hoisted_1, [
          _createElementVNode("div", _hoisted_2, [
            (!isEditing.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                  _createElementVNode("h1", _hoisted_4, _toDisplayString(policyName.value), 1)
                ]))
              : (_openBlock(), _createElementBlock("div", _hoisted_5, [
                  _withDirectives(_createElementVNode("input", {
                    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((tempPolicyName).value = $event)),
                    class: "border border-gray-300 rounded-md px-2 py-1 w-full"
                  }, null, 512), [
                    [_vModelText, tempPolicyName.value]
                  ])
                ]))
          ]),
          _createElementVNode("div", _hoisted_6, [
            (!isEditing.value)
              ? (_openBlock(), _createBlock(EditIcon, {
                  key: 0,
                  color: "#2f7f91",
                  class: "cursor-pointer w-8 h-8",
                  onClick: startEditing
                }))
              : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                  _createVNode(CheckIcon, {
                    color: "#2f7f91",
                    class: "cursor-pointer w-6 h-6",
                    onClick: saveEdit
                  }),
                  _createVNode(CloseIcon, {
                    color: "#DA1E28",
                    class: "cursor-pointer w-6 h-6",
                    onClick: cancelEdit
                  })
                ], 64))
          ])
        ]),
        _createElementVNode("div", _hoisted_7, [
          _createElementVNode("div", _hoisted_8, [
            _createVNode(SearchInput, {
              modelValue: textInput.value,
              "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((textInput).value = $event)),
              "search-function": onSearchByTabIndex,
              width: "80%",
              loading: loading.value,
              label: `Buscar médicos`,
              "hide-details": ""
            }, null, 8, ["modelValue", "loading"])
          ]),
          _createElementVNode("div", _hoisted_9, [
            _createVNode(OutlinedButton, {
              onClick: _cache[2] || (_cache[2] = ($event: any) => (openFilterModal())),
              text: 'Filtrar',
              loading: loading.value,
              width: "100%",
              height: "56px",
              "prepend-icon": _unref(mdiFilter)
            }, null, 8, ["loading", "prepend-icon"])
          ])
        ]),
        _createElementVNode("ul", _hoisted_10, [
          _createElementVNode("li", null, [
            _createElementVNode("button", {
              class: _normalizeClass(["w-max", tab.value === 1 ? activeClass.value : inactiveClass.value]),
              onClick: _cache[3] || (_cache[3] = ($event: any) => (changeTab(1)))
            }, " Autorizados ", 2)
          ]),
          _createElementVNode("li", null, [
            _createElementVNode("button", {
              class: _normalizeClass(["w-max", tab.value === 2 ? activeClass.value : inactiveClass.value]),
              onClick: _cache[4] || (_cache[4] = ($event: any) => (changeTab(2)))
            }, " Bloqueados ", 2)
          ]),
          _createElementVNode("li", null, [
            _createElementVNode("button", {
              class: _normalizeClass(["w-max", tab.value === 3 ? activeClass.value : inactiveClass.value]),
              onClick: _cache[5] || (_cache[5] = ($event: any) => (changeTab(3)))
            }, " Domínio ", 2)
          ])
        ]),
        _createElementVNode("div", _hoisted_11, [
          (tab.value === 1)
            ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
                _createVNode(Authorized, {
                  doctors: _unref(policyStore).authorizedDoctors,
                  loading: _unref(policyStore).isDoctorsLoading
                }, null, 8, ["doctors", "loading"])
              ]))
            : _createCommentVNode("", true),
          (tab.value === 2)
            ? (_openBlock(), _createElementBlock("div", _hoisted_13, [
                _createVNode(Blocked, {
                  doctors: _unref(policyStore).blockedDoctors,
                  loading: _unref(policyStore).isDoctorsLoading
                }, null, 8, ["doctors", "loading"])
              ]))
            : _createCommentVNode("", true),
          (tab.value === 3)
            ? (_openBlock(), _createElementBlock("div", _hoisted_14, [
                _createVNode(Domain)
              ]))
            : _createCommentVNode("", true)
        ]),
        (loading.value)
          ? (_openBlock(), _createBlock(Spinner, {
              key: 0,
              color: 'primary',
              size: '2rem',
              width: '0.5rem',
              class: "absolute inset-0 flex justify-center items-center bg-white bg-opacity-50 z-50"
            }))
          : _createCommentVNode("", true),
        (filterModalProps.value)
          ? (_openBlock(), _createBlock(FilterModal, {
              key: 1,
              "show-authorization-status-filter": true,
              modalStateProp: filterModalProps.value,
              onClose: closeFilterModal,
              onApplyFilters: applyFilters
            }, null, 8, ["modalStateProp"]))
          : _createCommentVNode("", true)
      ], 64))
    : _createCommentVNode("", true)
}
}

})